import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Flex,
  Icon,
  PageHeader,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
  useAutomationStore,
} from '@/shared/stores';
import { selectAutomationCodeLength } from '@/shared/stores/automation.store';
import { useQueryAutomation } from '@/generated/hooks/grimoire/v1/api';
import { V1AutomationStage } from '@/generated/hooks/grimoire/models/v1AutomationStage';
import type {
  Commonv1Value,
  V1Automation,
  V1CodeManifest,
} from '@/generated/hooks/grimoire/models';
import { useListRuns } from '@/generated/hooks/jeeves/v1/api';
import {
  type V1Run,
  type V1RunState,
  V1InvocationSource,
} from '@/generated/hooks/jeeves/models';
import { FilterBuilder } from '@/shared/utils';
import { RUN_STAGE } from '@/shared/constants';
import { ArchiveRunAlertDialog } from '@/modules/automation-details/components/AutomationRunsListCells';
import type { IVersionHistoryItemData } from '@/modules/automation-details/components/version-history/types';
import { useArchiveRun } from '@/modules/automation-details/use-archive-run';
import { getVersionBadgeText } from '@/modules/automation-details/components/version-history/utils';
import { getRoutePath } from '@/routes/util';
import {
  RunProgressIndicator,
  type IRunStepProgress,
} from '@/modules/automation-run/components/RunProgressIndicator';
import type { TAopViewLocationState } from '@/modules/aop-map/aop-view-state';
import type { TRunAgainLocationState } from '@/modules/automation/utils/run-again-state';
import { ERunHeaderControlAction } from '@/modules/automation-run/utils/run-utils';
import { AutomationIntegrationsPanel } from '@/modules/automation/components/AutomationIntegrationsPanel';
import { ManifestSopExportMenuItems } from '@/modules/automation/components/AutomationEditor/ManifestSopExportMenuItems';
import { AutomationOpenDraftDialog } from '@/modules/automation/components/AutomationOpenDraftDialog';
import { AutomationReactivateDialog } from '@/modules/automation/components/AutomationReactivateDialog';
import { PublishButton } from '@/modules/drafts';
import { RunHistoryDialog } from '@/modules/runs-list-panel';
import { TooltipActionButton } from '@/shared/components/TooltipActionButton';
import { AopMapViewToggle } from '@/shared/components/AopMapViewToggle';
import { AutomationHeaderTitle } from './AutomationHeaderTitle';
import { V1AutomationActivationState } from '@/generated/hooks/grimoire/models/v1AutomationActivationState';
import { usePermissions } from '@/shared/permissions';
import { useGateValue } from '@statsig/react-bindings';
import { useBackNavigation } from '@/shared/hooks/use-back-navigation';
import { FEATURE_FLAGS } from '@/shared/types/features';
import { TooltipDisabledButton } from '@/shared/components/TooltipDisabledButton';

/**
 * Props for the AutomationPageHeader component.
 */
export interface IAutomationPageHeaderProps {
  // --- Action visibility flags ---
  /** Show the version history button */
  showVersionHistory?: boolean;
  /** Show the run history panel button */
  showRunHistory?: boolean;
  /** Show the integrations panel button */
  showIntegrations?: boolean;
  /** Show the edit button (icon-only, navigates to editor — for run page) */
  showEdit?: boolean;
  /** Show the "Edit as Draft" text button (opens dialog — for published) */
  showEditAsDraft?: boolean;
  /** Show the "Run Again" button (run page) */
  showRunAgain?: boolean;
  /** Show the publish button (drafts) */
  showPublish?: boolean;
  /** Show the run/play button (editor) */
  showRun?: boolean;
  /** Show the pause/continue control button (run page) */
  showRunControl?: boolean;
  /** Action type for the run control button */
  runControlType?: ERunHeaderControlAction;
  /** Called when the run control button is clicked */
  onRunControl?: () => void;
  /** Whether the run control button is loading */
  isRunControlPending?: boolean;
  /** Whether a pause is in progress, from click through to the run reporting
   *  it. Holds the button's spinner up across that whole span, which outlasts
   *  the `PauseRun` request itself, instead of dropping to a static disabled
   *  Pause icon partway through. */
  isRunControlPausing?: boolean;
  /** Whether the run control button should be disabled */
  runControlDisabled?: boolean;

  // --- Title configuration ---
  /** Whether the title is editable with inline rename (draft editor) */
  isTitleEditable?: boolean;
  /**
   * Shows the automation's name in the header. Off on the run page, where the
   * name lives in the document card's own title bar instead.
   */
  showTitle?: boolean;
  /**
   * Switches between the document and the AOP map. Omit it to hide the
   * affordance — a surface whose document carries its own
   * `AutomationDocumentTitleBar` puts the toggle there instead.
   */
  onToggleMapView?: () => void;
  /** Whether the map is showing, which flips the toggle. */
  isMapView?: boolean;
  /** Whether the header is in read-only mode with back arrow (published editor) */
  isReadOnly?: boolean;

  // --- Center content (run page specific) ---
  /** Run progress percentage (0-100) */
  progress?: number;
  /**
   * Step counter for runs with addressable steps, shown as "Step i of x" in
   * place of the percentage. Null/undefined keeps the percentage.
   */
  stepProgress?: IRunStepProgress | null;
  /** Current run state for the progress indicator */
  runState?: V1RunState;
  /**
   * Whether the first AI completion has been received.
   * When false, the paused state in the progress indicator is suppressed.
   * Defaults to true for backward compatibility.
   */
  hasReceivedFirstCompletion?: boolean;

  // --- Run invocation (for showRun) ---
  /** Called when the run button is clicked */
  onRun?: () => void;
  /** Whether the automation is currently running */
  isRunning?: boolean;
  /** Whether the automation can be run */
  canRun?: boolean;

  // --- Internal test-run invocation (for showTest, Quill2 only) ---
  /** Whether to show the internal Test button, next to Run. */
  showTest?: boolean;
  /** Called when the Test button is clicked. */
  onTest?: () => void;
  /** Whether a test run is currently being fired. */
  isTesting?: boolean;
  /** Whether the automation can be tested. */
  canTest?: boolean;

  // --- Version history (shared state with editor layout) ---
  /** Whether the version history panel is open */
  isVersionHistoryOpen?: boolean;
  /** Actions available while reviewing version history. */
  versionHistoryActions?: ReactNode;
  /** Callback to open version history */
  onVersionHistoryClick?: () => void;
  /** Callback to close version history */
  onCloseVersionHistory?: () => void;
  /** The selected version in version history */
  selectedVersion?: IVersionHistoryItemData | null;
  /**
   * Version history without the name and back button, for a page whose own
   * header already carries both (the asset workspace): the version badge, then
   * the review actions and a close button.
   */
  isVersionHistoryCompact?: boolean;
  /**
   * Renders the header as the asset workspace's (Consistent_Layout), given
   * the workbench and map toggles for beside the name, and its actions.
   * Version history keeps its own bar.
   */
  renderWorkspaceHeader?: (parts: {
    titleActions: ReactNode;
    actions: ReactNode;
  }) => ReactNode;

  // --- Config ---
  /** Whether the integrations panel is in readonly mode (run page) */
  integrationsReadonly?: boolean;
  /** Whether the publish button should be disabled */
  publishDisabled?: boolean;

  // --- Overflow menu ---
  /**
   * Collapses the secondary actions (version history, run history, SOP export)
   * into a three-dot menu immediately left of Publish, instead of showing each
   * as its own toolbar button.
   *
   * Opt-in per page rather than always-on. The editor's toolbar is crowded and
   * benefits; the run page shows a different, smaller set where hiding Run
   * history behind an extra click would cost more than the density saves. The
   * menu never renders when it would be empty.
   */
  overflowActions?: boolean;
  /**
   * The built automation's manifest, enabling "Export" in the overflow menu.
   * Omitted (the run page, an unbuilt draft) leaves the entry out entirely —
   * there is nothing to serialise without a manifest.
   */
  exportManifest?: V1CodeManifest;
  /** Document heading and filename to fall back on when the manifest carries
   *  no title of its own. */
  exportTitleFallback?: string;

  /**
   * Pre-fetched automation from a parent. When provided, the header's
   * internal `useQueryAutomation` is disabled and this value is used
   * directly.
   */
  automation?: V1Automation;
  /**
   * Router state to carry into the run started by "Run again", e.g. to keep
   * the reader on the map they started it from.
   */
  runAgainState?: TAopViewLocationState;
  /** The current run's inputs, prefilled for review when "Run again" opens the inputs sheet. */
  runAgainInputs?: Record<string, Commonv1Value>;
  /** The run on the page, offered for archiving until it is archived. */
  archivableRun?: V1Run;
}

/**
 * Self-contained header for all automation-related pages.
 *
 * Reads automationId/draftId from route params, fetches automation and runs data
 * internally, and handles all action button logic (navigation, dialogs, tooltips).
 * The consumer only provides boolean visibility flags and a few context-specific props.
 *
 * Renders in three modes:
 * - **Editor mode**: Editable/readonly title, action buttons, run button
 * - **Run page mode**: Back arrow + title, progress indicator, run again/edit buttons
 * - **Version history mode**: Back arrow + title with badge, integrations only
 *
 * @param {IAutomationPageHeaderProps} props - Component props
 * @returns {JSX.Element | null} The page header, or null if no automationId is available
 */
export function AutomationPageHeader({
  // Action visibility
  showVersionHistory = false,
  showRunHistory = false,
  showIntegrations = false,
  showEdit = false,
  showEditAsDraft = false,
  showRunAgain = false,
  showPublish = false,
  showRun = false,
  showRunControl = false,
  // Title
  isTitleEditable = false,
  showTitle = true,
  onToggleMapView,
  isMapView = false,
  isReadOnly = false,
  // Center content
  progress,
  stepProgress,
  runState,
  hasReceivedFirstCompletion = true,
  // Run invocation
  onRun,
  isRunning = false,
  canRun: canRunProp,
  // Internal test-run invocation
  showTest = false,
  onTest,
  isTesting = false,
  canTest = false,
  runControlType,
  onRunControl,
  isRunControlPending = false,
  isRunControlPausing = false,
  runControlDisabled = false,
  // Version history
  isVersionHistoryOpen = false,
  versionHistoryActions,
  onVersionHistoryClick,
  onCloseVersionHistory,
  selectedVersion,
  isVersionHistoryCompact = false,
  renderWorkspaceHeader,
  // Config
  integrationsReadonly = false,
  publishDisabled = false,
  automation: automationProp,
  // Overflow menu
  overflowActions = false,
  exportManifest,
  exportTitleFallback,
  runAgainState,
  runAgainInputs,
  archivableRun,
}: IAutomationPageHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('automation');
  const { hasPermission } = usePermissions();

  // --- Derive IDs from route params and store ---
  const params = useParams<{
    automationId?: string;
    draftId?: string;
    runId?: string;
  }>();
  const isDraft = Boolean(params.draftId);
  const automationId = params.draftId || params.automationId || '';
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // --- Store selectors ---
  const codeLength = useAutomationStore(selectAutomationCodeLength);
  const canRun = canRunProp ?? (codeLength ?? 0) > 0;

  // --- Edit as Draft dialog ---
  const {
    value: isDraftDialogOpen,
    setTrue: openDraftDialog,
    setFalse: closeDraftDialog,
  } = useBoolean(false);

  // --- Run history panel open state (used to lazy-fetch runs) ---
  const { value: isRunHistoryOpen, setValue: setRunHistoryOpen } =
    useBoolean(false);

  // Count the same runs the dialog shows (matching stage, excluding test runs)
  // so the run history button is enabled exactly when the dialog would have rows.
  const { data: runsCountData, isLoading: isRunsCountLoading } = useListRuns(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    {
      page_size: 1,
      filter: FilterBuilder.create()
        .equals('stage', isDraft ? RUN_STAGE.DRAFT : RUN_STAGE.PUBLISHED)
        .notEquals(
          'invocation_source',
          V1InvocationSource.INVOCATION_SOURCE_TEST,
        )
        .build(),
    },
    {
      query: {
        enabled:
          showRunHistory &&
          Boolean(automationId && currentOrganizationId && currentWorkspaceId),
      },
    },
  );
  const hasRuns = (runsCountData?.data?.runs?.length ?? 0) > 0;
  // Stay enabled while loading to avoid a disabled flash; disable once confirmed empty.
  const runHistoryDisabled = !isRunsCountLoading && !hasRuns;

  // --- Overflow menu ---
  // Which secondary actions have moved into the three-dot menu. Each is only
  // "in the menu" if the page asked for the menu AND for that action, so a
  // caller that opts in without any of them (or opts out entirely, as the run
  // page does) gets exactly the toolbar it had before.
  const versionHistoryInMenu =
    overflowActions && showVersionHistory && Boolean(onVersionHistoryClick);
  const runHistoryInMenu = overflowActions && showRunHistory;
  const exportInMenu = overflowActions && Boolean(exportManifest);
  // An empty three-dot menu is worse than no menu at all.
  const hasOverflowMenu =
    versionHistoryInMenu || runHistoryInMenu || exportInMenu;

  // --- Fetch automation data (title, code, updatedAt) ---
  // Disabled when a parent passes `automation` as a prop.
  const { data: internalAutomationData } = useQueryAutomation(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    {
      stage: isDraft
        ? V1AutomationStage.AUTOMATION_STAGE_DRAFT
        : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
    },
    {
      query: {
        retry: false,
        retryOnMount: true,
        enabled: Boolean(
          automationId &&
            currentOrganizationId &&
            currentWorkspaceId &&
            !automationProp,
        ),
        staleTime: 60000,
      },
    },
  );
  const automation = automationProp ?? internalAutomationData?.data;

  // --- Check if the user can edit as draft ---
  const canEditAsDraft = hasPermission(['ForkPublishedAutomation'], {
    organization: currentOrganizationId,
    workspace: currentWorkspaceId,
  });

  // --- Check if user can publish an automation ---
  const canPublishAutomation = hasPermission(['PublishAutomation'], {
    organization: currentOrganizationId,
    workspace: currentWorkspaceId,
  });

  const isConsistentLayout = useGateValue(FEATURE_FLAGS.CONSISTENT_LAYOUT);
  const editorPath = isDraft
    ? getRoutePath('DRAFT_EDITOR', {
        orgId: currentOrganizationId,
        workspaceId: currentWorkspaceId,
        draftId: automationId,
      })
    : getRoutePath('AUTOMATION_DETAILS', {
        orgId: currentOrganizationId,
        workspaceId: currentWorkspaceId,
        automationId,
      });
  // Runs are opened from Activity too, so back follows history; the editor is only the fallback.
  const { goBack: goBackFromRun } = useBackNavigation(editorPath);

  // --- Reactivate dialog ---
  const {
    value: isReactivateDialogOpen,
    setTrue: openReactivateDialog,
    setFalse: closeReactivateDialog,
  } = useBoolean(false);

  // --- Draft check query (for Edit as Draft override warning) ---
  const { data: draftCheckData, isLoading: isDraftCheckLoading } =
    useQueryAutomation(
      currentOrganizationId,
      currentWorkspaceId,
      automationId,
      { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
      {
        query: {
          retry: false,
          retryOnMount: true,
          enabled: Boolean(
            currentOrganizationId &&
              currentWorkspaceId &&
              automationId &&
              !isDraft &&
              isDraftDialogOpen,
          ),
        },
      },
    );

  // --- Navigation handlers ---
  const handleNavigateToEditor = () => navigate(editorPath);

  const { archiveRun, isArchiving } = useArchiveRun(automationId);
  const { value: isArchiveDialogOpen, setValue: setIsArchiveDialogOpen } =
    useBoolean(false);
  const leaveRun = isConsistentLayout ? goBackFromRun : handleNavigateToEditor;
  const handleArchiveConfirm = async () => {
    setIsArchiveDialogOpen(false);
    if (archivableRun && (await archiveRun(archivableRun))) leaveRun();
  };

  const handleRunAgain = () => {
    const state: TRunAgainLocationState | undefined = runAgainInputs
      ? { ...runAgainState, runAgainInputs }
      : runAgainState;
    if (isDraft) {
      navigate(
        getRoutePath('DRAFT_PRE_RUN', {
          orgId: currentOrganizationId,
          workspaceId: currentWorkspaceId,
          draftId: automationId,
        }),
        { state },
      );
    } else {
      navigate(
        getRoutePath('AUTOMATION_PRE_RUN', {
          orgId: currentOrganizationId,
          workspaceId: currentWorkspaceId,
          automationId,
        }),
        { state },
      );
    }
  };

  // --- Early returns ---
  if (!automationId) {
    return null;
  }

  // --- Version history mode ---
  if (isVersionHistoryOpen) {
    const badgeText = getVersionBadgeText(selectedVersion);

    if (isVersionHistoryCompact) {
      // The workspace header's back exits history, so the bar only carries the review actions.
      if (!versionHistoryActions) return null;
      return (
        <Flex
          align="center"
          justify="end"
          gap={2}
          width="full"
          // Inset like the document panel in history mode, so the actions line up with the card.
          className="min-h-9 shrink-0 px-2"
          data-testid="version-history-compact-header"
        >
          {versionHistoryActions}
        </Flex>
      );
    }

    return (
      <PageHeader
        className="w-full h-auto bg-transparent border-none shadow-none"
        left={
          <Flex align="center" gap={4}>
            <Tooltip delayDuration={200} variant="primary">
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={onCloseVersionHistory}
                  data-testid="version-history-back-button"
                  aria-label={t('automation.header.tooltip.exitVersionHistory')}
                >
                  <Icon type="ArrowLeft" size="sm" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('automation.header.tooltip.exitVersionHistory')}
              </TooltipContent>
            </Tooltip>
            <Flex align="center" gap={4}>
              <Text level="base" weight="medium" className="whitespace-nowrap">
                {automation?.display_name}
              </Text>
              {badgeText && (
                <Badge
                  variant="default"
                  className="bg-primary/80 text-primary-foreground"
                  data-testid="version-history-badge"
                >
                  {badgeText}
                </Badge>
              )}
            </Flex>
          </Flex>
        }
        right={versionHistoryActions}
      />
    );
  }

  // --- Determine which center content to show ---
  const hasProgressIndicator = progress !== undefined && runState !== undefined;

  // --- Determine if we're in run page mode (not editor) ---
  const isRunPageMode = !isTitleEditable && !isReadOnly;

  // --- Whole-automation test workbench ---
  // The stage follows the page: a draft is tested against the draft code, a
  // published automation against published.
  const canOpenWorkbench = Boolean(
    automationId && currentOrganizationId && currentWorkspaceId,
  );
  const handleOpenWorkbench = () => {
    const path = getRoutePath('AUTOMATION_WORKBENCH', {
      orgId: currentOrganizationId,
      workspaceId: currentWorkspaceId,
      automationId,
    });
    navigate(`${path}?stage=${isDraft ? 'DRAFT' : 'PUBLISHED'}`);
  };

  // --- Title area (always uses AutomationHeaderTitle) ---
  const renderTitle = () => (
    <AutomationHeaderTitle
      showTitle={showTitle}
      updatedAt={automation?.update_time}
      title={automation?.display_name}
      automationId={automationId}
      isDraft={isDraft}
      isTitleEditable={isTitleEditable}
      activationState={automation?.activation_state}
      onBack={isRunPageMode ? leaveRun : undefined}
      onOpenWorkbench={canOpenWorkbench ? handleOpenWorkbench : undefined}
      onToggleMapView={onToggleMapView}
      isMapView={isMapView}
    />
  );

  const headerActions = (
    <>
      {/* Button order follows Figma: Version History → Run History →
          Integrations → Edit → Run Again → Pause/Continue → Publish →
          Run/Play. When `overflowActions` is on, the first two collapse
          into the three-dot menu that leads the row; Integrations keeps
          its own button to the menu's right. */}

      {hasOverflowMenu && (
        // modal={false} so opening the menu doesn't engage Radix's
        // scroll lock, which pads <body> and shifts the page's left
        // edge — same reason as the runs-list row menu.
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="outline"
              aria-label={t('automation.header.moreActions')}
              data-testid="header-overflow-menu-trigger"
            >
              <Icon type="EllipsisVertical" size="sm" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {versionHistoryInMenu && (
              <DropdownMenuItem
                data-testid="overflow-version-history"
                onSelect={onVersionHistoryClick}
              >
                <Icon type="History" size="sm" />
                {t('automation.header.tooltip.versionHistory')}
              </DropdownMenuItem>
            )}
            {runHistoryInMenu && (
              <DropdownMenuItem
                data-testid="overflow-run-history"
                // Carries the "this automation has never run" state the
                // trigger button used to own.
                disabled={runHistoryDisabled}
                onSelect={() => setRunHistoryOpen(true)}
              >
                <Icon type="ListVideo" size="sm" />
                {t('automation.header.tooltip.runHistory')}
              </DropdownMenuItem>
            )}
            {exportInMenu && exportManifest && (
              <>
                {(versionHistoryInMenu || runHistoryInMenu) && (
                  <DropdownMenuSeparator />
                )}
                <ManifestSopExportMenuItems
                  manifest={exportManifest}
                  titleFallback={exportTitleFallback}
                />
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {showVersionHistory && onVersionHistoryClick && !versionHistoryInMenu && (
        <TooltipActionButton
          id="run-page-version-history-button"
          data-testid="version-history-button"
          tooltip={t('automation.header.tooltip.versionHistory')}
          icon="History"
          onClick={onVersionHistoryClick}
        />
      )}

      {showRunHistory && (
        <RunHistoryDialog
          automationId={automationId}
          isDraft={isDraft}
          isOpen={isRunHistoryOpen}
          onOpenChange={setRunHistoryOpen}
          onRun={onRun}
          isRunLoading={isRunning}
          disabled={runHistoryDisabled}
          // The dialog itself must stay mounted either way — the
          // overflow menu item drives its controlled open state.
          showTrigger={!runHistoryInMenu}
        />
      )}

      {showIntegrations && (
        <AutomationIntegrationsPanel
          automationId={automationId}
          isDraft={isDraft}
          readonly={integrationsReadonly}
        />
      )}

      {showEdit && (
        <TooltipActionButton
          id="run-page-edit-button"
          data-testid="edit-button"
          tooltip={t('automation.header.tooltip.edit')}
          icon="PencilLine"
          onClick={handleNavigateToEditor}
        />
      )}

      {showEditAsDraft && canEditAsDraft && (
        <TooltipActionButton
          id="run-page-edit-as-draft-button"
          tooltip={t('automation.header.tooltip.editAsDraft')}
          icon="PencilLine"
          onClick={openDraftDialog}
          data-testid="run-page-edit-button"
          size="icon"
          variant="outline"
        />
      )}

      {showEditAsDraft && !canEditAsDraft && (
        <TooltipDisabledButton
          tooltip={t('automation.header.editAsDraftDisabled')}
          icon="PencilLine"
          data-testid="run-page-edit-button"
        />
      )}

      {archivableRun && !archivableRun.archived_time && (
        <>
          <TooltipActionButton
            id="run-page-archive-button"
            data-testid="run-page-archive-button"
            tooltip={t('automation.runs.archiveRun')}
            icon="Archive"
            onClick={() => setIsArchiveDialogOpen(true)}
            disabled={isArchiving}
          />
          <ArchiveRunAlertDialog
            open={isArchiveDialogOpen}
            onOpenChange={setIsArchiveDialogOpen}
            onConfirm={() => void handleArchiveConfirm()}
          />
        </>
      )}

      {showRunAgain && (
        <TooltipActionButton
          id="run-page-run-again-button"
          data-testid="run-again-button"
          tooltip={t('automation.header.tooltip.runAgain')}
          icon="Play"
          onClick={handleRunAgain}
          variant="default"
          size="icon"
        />
      )}

      {showRunControl && runControlType && (
        <TooltipActionButton
          id="run-page-run-control-button"
          data-testid="run-control-button"
          tooltip={
            runControlType !== ERunHeaderControlAction.PAUSE
              ? t('automation.header.tooltip.continueRun')
              : isRunControlPausing
                ? t('automation.header.tooltip.pausingRun')
                : t('automation.header.tooltip.pauseRun')
          }
          icon={
            runControlType === ERunHeaderControlAction.PAUSE ? 'Pause' : 'Play'
          }
          onClick={onRunControl}
          disabled={runControlDisabled}
          // Pausing spans the request, the wait for the run to report
          // it, and the unwind; dropping the spinner at any seam in
          // there reads as "nothing happened". Resume is just the
          // request.
          isLoading={
            runControlType === ERunHeaderControlAction.PAUSE
              ? isRunControlPausing
              : isRunControlPending
          }
          variant="default"
          size="icon"
        />
      )}

      {showPublish && isDraft && canPublishAutomation && (
        <PublishButton
          data-tour-id="run-page-publish-button"
          automationId={automationId}
          variant="minimal"
          disabled={publishDisabled || isRunning}
        />
      )}

      {showPublish && isDraft && !canPublishAutomation && (
        <TooltipDisabledButton
          tooltip={t('automation.header.tooltip.publishDisabled')}
          icon="Rocket"
          data-testid="publish-button"
        />
      )}

      {showTest && (
        <TooltipActionButton
          data-testid="test-automation-button"
          tooltip={
            canTest
              ? t('automation.header.tooltip.test')
              : t('automation.header.tooltip.testDisabledNoCode')
          }
          icon="FlaskConical"
          onClick={onTest}
          disabled={isTesting || !canTest}
          isLoading={isTesting}
          variant="outline"
          size="icon"
        />
      )}
      {showRun && (
        <TooltipActionButton
          data-tour-id="run-automation-button"
          data-testid="run-automation-button"
          tooltip={
            canRun
              ? t('automation.header.tooltip.run')
              : t('automation.header.tooltip.runDisabledNoCode')
          }
          icon="Play"
          onClick={onRun}
          disabled={
            isRunning ||
            !canRun ||
            automation?.activation_state ===
              V1AutomationActivationState.AUTOMATION_ACTIVATION_STATE_DEACTIVATED
          }
          isLoading={isRunning}
          variant="default"
          size="icon"
        />
      )}
      {automation?.activation_state ===
        V1AutomationActivationState.AUTOMATION_ACTIVATION_STATE_DEACTIVATED && (
        <TooltipActionButton
          data-testid="reactivate-automation-button"
          tooltip={t('automation.header.tooltip.reactivate')}
          onClick={openReactivateDialog}
          variant="default"
          size="default"
        >
          {t('automation.reactivate')}
        </TooltipActionButton>
      )}
    </>
  );

  return (
    <>
      {renderWorkspaceHeader ? (
        renderWorkspaceHeader({
          titleActions: (canOpenWorkbench || onToggleMapView) && (
            <>
              {canOpenWorkbench && (
                <TooltipActionButton
                  data-testid="test-automation-workbench-button"
                  tooltip={t('automation.header.tooltip.testAutomation')}
                  aria-label={t('automation.header.tooltip.testAutomation')}
                  icon="FlaskConical"
                  variant="ghost"
                  size="icon"
                  onClick={handleOpenWorkbench}
                />
              )}
              {onToggleMapView && (
                <AopMapViewToggle
                  onToggle={onToggleMapView}
                  isMapView={isMapView}
                />
              )}
            </>
          ),
          actions: headerActions,
        })
      ) : (
        <PageHeader
          className="w-full h-auto bg-transparent border-none shadow-none"
          left={renderTitle()}
          center={
            hasProgressIndicator ? (
              <Flex
                data-tour-id="run-page-progress-indicator"
                align="center"
                justify="center"
                gap={2}
                paddingX={5}
                width="full"
              >
                <RunProgressIndicator
                  runState={runState}
                  progress={progress!}
                  stepProgress={stepProgress}
                  hasReceivedFirstCompletion={hasReceivedFirstCompletion}
                />
              </Flex>
            ) : undefined
          }
          right={
            <Flex align="center" gap={2}>
              {headerActions}
            </Flex>
          }
        />
      )}
      {showEditAsDraft && automationId && (
        <AutomationOpenDraftDialog
          open={isDraftDialogOpen}
          onClose={closeDraftDialog}
          automationId={automationId}
          isOverride={Boolean(draftCheckData?.data?.version)}
          isLoading={isDraftCheckLoading}
          isDeactivated={
            automation?.activation_state ===
            V1AutomationActivationState.AUTOMATION_ACTIVATION_STATE_DEACTIVATED
          }
        />
      )}
      <AutomationReactivateDialog
        open={isReactivateDialogOpen}
        onClose={closeReactivateDialog}
        title={automation?.display_name || ''}
        automationId={automationId}
      />
    </>
  );
}
